import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Trophy, Medal, Award, Star, Calculator, Eye, Save, Sparkles, Trash2, FileText } from 'lucide-react';
import { periodLabel } from '../components/BadgeAnnouncement';
import { generateBadgeReportPdf } from '../utils/badgeReportPdf';

const API = `${window.location.origin}/api`;

const RANK_ICON = { 1: Trophy, 2: Medal, 3: Award };
const RANK_COLOR = { 1: 'text-amber-600', 2: 'text-slate-500', 3: 'text-orange-700' };

const defaultPeriod = () => {
  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
};

export default function BadgeSettings() {
  const { user } = useContext(AuthContext);
  const [settings, setSettings] = useState(null);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [period, setPeriod] = useState(defaultPeriod());
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mentionUser, setMentionUser] = useState('');
  const [mentionReason, setMentionReason] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reporting, setReporting] = useState(false);

  const downloadReport = async () => {
    setReporting(true);
    try {
      const res = await axios.get(`${API}/badges/report`);
      if (!res.data || (res.data.totalBadges || 0) === 0) {
        toast.info('Henüz rozet kaydı yok, rapor oluşturulamadı.');
        return;
      }
      await generateBadgeReportPdf(res.data);
      toast.success('Rozet raporu PDF olarak indirildi.');
    } catch (e) {
      toast.error('Rapor oluşturulurken hata oluştu.');
    } finally {
      setReporting(false);
    }
  };

  const loadAll = async () => {
    try {
      const [sRes, tRes, uRes] = await Promise.all([
        axios.get(`${API}/badges/settings`),
        axios.get(`${API}/tags`),
        axios.get(`${API}/users`),
      ]);
      setSettings(sRes.data);
      setTags((tRes.data || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr')));
      setUsers((uRes.data || []).filter(u => u.status !== 'deleted'));
    } catch (e) {
      toast.error('Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const loadPreview = async (p) => {
    try {
      const res = await axios.get(`${API}/badges?period=${p}`);
      setPreview(res.data.badges || []);
    } catch (e) {
      setPreview([]);
    }
  };
  useEffect(() => { if (!loading) loadPreview(period); }, [period, loading]);

  const updateCategory = (tagId, patch) => {
    setSettings(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [tagId]: { evaluated: false, direction: 'highest', ...(prev.categories?.[tagId] || {}), ...patch },
      },
    }));
  };

  const togglePublishRank = (tagId, rank) => {
    setSettings(prev => {
      const cur = prev.publish?.[tagId] || [];
      const next = cur.includes(rank) ? cur.filter(r => r !== rank) : [...cur, rank].sort((a, b) => a - b);
      return { ...prev, publish: { ...prev.publish, [tagId]: next } };
    });
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/badges/settings`, {
        categories: settings.categories,
        topN: settings.topN,
        dashboardEnabled: settings.dashboardEnabled,
        congratsTemplate: settings.congratsTemplate,
        publish: settings.publish,
        mentionPublished: settings.mentionPublished,
      });
      toast.success('Ayarlar kaydedildi');
    } catch (e) {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const computeNow = async () => {
    try {
      const res = await axios.post(`${API}/badges/compute?period=${period}`);
      toast.success(`${periodLabel(period)} için ${res.data.count} performans rozeti hesaplandı`);
      loadPreview(period);
    } catch (e) {
      toast.error('Hesaplama başarısız');
    }
  };

  const addMention = async () => {
    if (!mentionUser) { toast.error('Kullanıcı seçin'); return; }
    try {
      const res = await axios.post(`${API}/badges/mention`, {
        period, userIds: [mentionUser], reason: mentionReason || null,
      });
      if (res.data.count > 0) toast.success('Mansiyon rozeti atandı');
      else toast.info('Kullanıcı bu dönemde zaten mansiyon almış');
      setMentionUser(''); setMentionReason('');
      loadPreview(period);
    } catch (e) {
      toast.error('Mansiyon atanamadı');
    }
  };

  const removeMention = async (id) => {
    try {
      await axios.delete(`${API}/badges/mention/${id}`);
      toast.success('Mansiyon kaldırıldı');
      loadPreview(period);
    } catch (e) {
      toast.error('Kaldırma başarısız');
    }
  };

  const deletePeriod = async () => {
    try {
      const res = await axios.delete(`${API}/badges/period/${period}`);
      toast.success(`${periodLabel(period)} silindi (${res.data.deleted} rozet)`);
      setShowDeleteConfirm(false);
      loadPreview(period);
    } catch (e) {
      toast.error('Silme başarısız');
    }
  };

  if (loading || !settings) {
    return <div className="flex items-center justify-center h-64"><div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const evaluatedTags = tags.filter(t => settings.categories?.[t.id]?.evaluated);
  const perfPreview = preview
    .filter(b => b.type === 'performance')
    .sort((a, b) => (a.tagName || '').localeCompare(b.tagName || '', 'tr') || (a.rank || 99) - (b.rank || 99));
  const mentionPreview = preview
    .filter(b => b.type === 'mention')
    .sort((a, b) => (a.userName || '').localeCompare(b.userName || '', 'tr'));

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="badge-settings-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Performans Rozetleri</h1>
            <p className="text-sm text-gray-500">Kategori değerlendirme, dashboard yayını ve mansiyon ayarları</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={downloadReport} disabled={reporting} variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50" data-testid="badge-report-pdf">
            <FileText className="w-4 h-4 mr-2" />{reporting ? 'Hazırlanıyor...' : 'Rapor (PDF)'}
          </Button>
          <Button onClick={saveSettings} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="badge-save-settings">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
          </Button>
        </div>
      </div>

      {/* Kategori değerlendirme */}
      <div className="glass rounded-2xl p-6 mb-6" data-testid="badge-category-section">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Değerlendirilecek Kategoriler</h2>
        <p className="text-sm text-gray-500 mb-4">Hangi kategoriler için rozet hesaplanacağını ve "en iyi" yönünü belirleyin. Değişiklikler bir sonraki ayın 1'inde geçerli olur.</p>
        <div className="space-y-3">
          {tags.map(tag => {
            const c = settings.categories?.[tag.id] || { evaluated: false, direction: 'highest' };
            return (
              <div key={tag.id} className="flex flex-wrap items-center gap-4 p-3 rounded-xl border border-gray-100 bg-white">
                <div className="flex items-center gap-3 flex-1 min-w-[160px]">
                  <Switch checked={!!c.evaluated} onCheckedChange={(v) => updateCategory(tag.id, { evaluated: v })} data-testid={`badge-eval-${tag.id}`} />
                  <span className="font-semibold text-gray-800">{tag.name}</span>
                  <span className="text-xs text-gray-400">({tag.quantityType || 'metrik'})</span>
                </div>
                {c.evaluated && (
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-gray-500">En iyi:</Label>
                    <Select value={c.direction || 'highest'} onValueChange={(v) => updateCategory(tag.id, { direction: v })}>
                      <SelectTrigger className="w-36 h-9" data-testid={`badge-dir-${tag.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="highest">En yüksek değer</SelectItem>
                        <SelectItem value="lowest">En düşük değer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Label className="text-sm">Kaç kişi ödüllendirilsin (Top N):</Label>
          <Input type="number" min={1} max={10} value={settings.topN}
            onChange={(e) => setSettings(p => ({ ...p, topN: parseInt(e.target.value) || 3 }))}
            className="w-20 h-9" data-testid="badge-topn" />
          <span className="text-xs text-gray-400">Her kategoride en iyi kaç kişiye rozet verileceğini belirler (örn. 3 → 1., 2. ve 3.).</span>
        </div>
      </div>

      {/* Dashboard yayın ayarları */}
      <div className="glass rounded-2xl p-6 mb-6" data-testid="badge-publish-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Dashboard Yayın Ayarları</h2>
            <p className="text-sm text-gray-500">Hangi rozetlerin dashboard'da duyurulacağını seçin (profilde tüm rozetler otomatik görünür).</p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">Yayın aktif</Label>
            <Switch checked={!!settings.dashboardEnabled} onCheckedChange={(v) => setSettings(p => ({ ...p, dashboardEnabled: v }))} data-testid="badge-dashboard-enabled" />
          </div>
        </div>
        {evaluatedTags.length === 0 ? (
          <p className="text-sm text-gray-400">Önce değerlendirilecek kategori seçin.</p>
        ) : (
          <div className="space-y-3">
            {evaluatedTags.map(tag => (
              <div key={tag.id} className="flex flex-wrap items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white">
                <span className="font-semibold text-gray-800 min-w-[120px]">{tag.name}</span>
                <span className="text-xs text-gray-400">Göster:</span>
                {Array.from({ length: settings.topN || 3 }, (_, i) => i + 1).map(rank => {
                  const active = (settings.publish?.[tag.id] || []).includes(rank);
                  const Icon = RANK_ICON[rank] || Award;
                  return (
                    <button key={rank} onClick={() => togglePublishRank(tag.id, rank)}
                      data-testid={`badge-publish-${tag.id}-${rank}`}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-300' : 'bg-gray-100 text-gray-400'}`}>
                      <Icon className="w-4 h-4" />{rank}.
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          <Label className="text-sm mb-1 block">Tebrik mesajı şablonu ({'{name}'} = kullanıcı adı)</Label>
          <Input value={settings.congratsTemplate} onChange={(e) => setSettings(p => ({ ...p, congratsTemplate: e.target.value }))}
            className="max-w-md" data-testid="badge-congrats" />
        </div>
        <div className="flex items-center gap-2 mt-4">
          <Switch checked={!!settings.mentionPublished} onCheckedChange={(v) => setSettings(p => ({ ...p, mentionPublished: v }))} data-testid="badge-mention-published" />
          <Label className="text-sm">Mansiyon rozetlerini dashboard'da göster</Label>
        </div>
      </div>

      {/* Dönem + Hesaplama + Önizleme */}
      <div className="glass rounded-2xl p-6 mb-6" data-testid="badge-preview-section">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Eye className="w-5 h-5 text-amber-600" /> Önizleme & Hesaplama</h2>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto sm:ml-auto">
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Label className="text-sm flex-shrink-0">Dönem:</Label>
              <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="flex-1 sm:flex-none sm:w-44 h-9" data-testid="badge-period" />
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button onClick={computeNow} variant="outline" className="flex-1 sm:flex-none border-amber-300 text-amber-700" data-testid="badge-compute">
                <Calculator className="w-4 h-4 mr-2" />Hesapla
              </Button>
              <Button onClick={() => setShowDeleteConfirm(true)} variant="outline" className="flex-1 sm:flex-none border-red-300 text-red-600 hover:bg-red-50"
                disabled={preview.length === 0} data-testid="badge-delete-period">
                <Trash2 className="w-4 h-4 mr-2" />Sil
              </Button>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-400 mb-4">Üretimde rozetler her ayın 1'inde otomatik hesaplanır. Buradan manuel olarak hesaplayıp önizleyebilirsiniz.</p>

        {perfPreview.length === 0 && mentionPreview.length === 0 ? (
          <p className="text-sm text-gray-400">{periodLabel(period)} için hesaplanmış rozet yok. "Hesapla" ile oluşturun.</p>
        ) : (
          <div className="space-y-4">
            {Object.values(perfPreview.reduce((acc, b) => {
              const key = b.tagId || b.tagName || 'other';
              if (!acc[key]) acc[key] = { tagName: b.tagName, items: [] };
              acc[key].items.push(b);
              return acc;
            }, {}))
              .sort((a, b) => (a.tagName || '').localeCompare(b.tagName || '', 'tr'))
              .map((group) => (
                <div key={group.tagName} data-testid="badge-preview-category-row">
                  <div className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">{group.tagName}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {group.items.sort((a, b) => (a.rank || 99) - (b.rank || 99)).map((b) => {
                      const Icon = RANK_ICON[b.rank] || Trophy;
                      return (
                        <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-white">
                          <Icon className={`w-7 h-7 ${RANK_COLOR[b.rank] || 'text-amber-600'}`} />
                          <div>
                            <div className="text-sm font-semibold text-gray-900">{b.userName}</div>
                            <div className="text-xs text-gray-500">{b.tagName} · {b.rank}. · {b.valueDisplay} {b.metric}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

            {mentionPreview.length > 0 && (
              <div data-testid="badge-preview-mention-row">
                <div className="text-xs font-bold text-fuchsia-700 uppercase tracking-wide mb-2">Mansiyon</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {mentionPreview.map((b) => (
                    <div key={b.id} className="flex items-center gap-3 p-3 rounded-xl border border-fuchsia-100 bg-fuchsia-50">
                      <Star className="w-7 h-7 text-fuchsia-500" />
                      <div className="flex-1">
                        <div className="text-sm font-semibold text-gray-900">{b.userName}</div>
                        <div className="text-xs text-gray-500">Mansiyon{b.reason ? ` · ${b.reason}` : ''}</div>
                      </div>
                      <button onClick={() => removeMention(b.id)} className="text-xs text-red-500 hover:underline">Kaldır</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mansiyon atama */}
      <div className="glass rounded-2xl p-6" data-testid="badge-mention-section">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-1"><Sparkles className="w-5 h-5 text-fuchsia-500" /> Mansiyon Rozeti Ata</h2>
        <p className="text-sm text-gray-500 mb-4">Kategoriden bağımsız olarak, üstün çalışma için seçtiğiniz kullanıcıya {periodLabel(period)} mansiyon rozeti verin.</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs mb-1 block">Kullanıcı</Label>
            <Select value={mentionUser} onValueChange={setMentionUser}>
              <SelectTrigger className="w-56" data-testid="badge-mention-user"><SelectValue placeholder="Kullanıcı seçin" /></SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs mb-1 block">Gerekçe (opsiyonel)</Label>
            <Input value={mentionReason} onChange={(e) => setMentionReason(e.target.value)} placeholder="Üstün kalite, ekstra çaba..." data-testid="badge-mention-reason" />
          </div>
          <Button onClick={addMention} className="bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="badge-mention-add">
            <Star className="w-4 h-4 mr-2" />Ata
          </Button>
        </div>
      </div>

      {/* Dönem silme onayı */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent data-testid="badge-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{periodLabel(period)} rozetleri silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu dönemin <strong>{perfPreview.length} performans</strong> ve <strong>{mentionPreview.length} mansiyon</strong> rozeti kalıcı olarak silinecek.
              Performans rozetlerini "Hesapla" ile yeniden oluşturabilirsiniz; mansiyonların yeniden atanması gerekir. Bu işlem geri alınamaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="badge-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={deletePeriod} className="bg-red-600 hover:bg-red-700" data-testid="badge-delete-confirm">
              Evet, Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
